import moment from "moment";
import momentTZ from "moment-timezone";

export const DateTimeFormat = {
  shortDateTimeFormat: "DD MMM HH:mm",
  shortDateTimeYearFormat: "D MMM YYYY, h:mm a",
  fullDateTimeFormat: "DD MMM YYYY HH:mm",
  dateFormat: "DD MMM YYYY",
  timeFormat: "HH:mm",
};

export const BACKEND_DATETIME_FORMATS = ["YYYY-MM-DD HH:mm:ss", "YYYY-MM-DD hh:mm:ss"];

export const PREFERRED_WALL_CLOCK_DATETIME_FORMAT = "YYYY-MM-DD HH:mm:ss";

const PERMISSIONS_STORAGE_KEY = "cargobase_userpermissions";
const DEFAULT_DATETIME_FALLBACK = "-";

export type DatetimeInput = string | number | Date | momentTZ.Moment | null | undefined;

export type ParseBackendDatetimeOptions = {
  /** When true (default), naive API string is a UTC instant. */
  assumeUtc?: boolean;
  /** When assumeUtc is false, which TZ the naive digits represent before converting to target. */
  sourceTimezone?: string;
};

export type FormatInTimezoneOptions = ParseBackendDatetimeOptions & {
  fallback?: string;
  includeTimezoneLabel?: boolean;
  /** Override resolved preferred TZ when calling formatInPreferredTimezone. */
  timezone?: string;
};

function isEmptyDatetimeValue(value: unknown): boolean {
  return value === null || value === undefined || value === "" || value === " ";
}

type ParseNaiveFormatFn = (input: string, format: string) => momentTZ.Moment; // eslint-disable-line no-unused-vars

function parseWithNaiveFormats(value: DatetimeInput, parse: ParseNaiveFormatFn): momentTZ.Moment | null {
  if (momentTZ.isMoment(value) && value.isValid()) {
    return value;
  }

  if (typeof value !== "string") {
    const parsed = momentTZ.utc(value);
    return parsed.isValid() ? parsed : null;
  }

  const parsedFromFormat = BACKEND_DATETIME_FORMATS.reduce<momentTZ.Moment | null>((result, format) => {
    if (result) {
      return result;
    }

    const parsed = parse(value, format);
    return parsed.isValid() ? parsed : null;
  }, null);

  if (parsedFromFormat) {
    return parsedFromFormat;
  }

  const looksLikeIso = /^\d{4}-\d{2}-\d{2}(T|\s)/.test(value) || value.includes("Z");
  if (!looksLikeIso) {
    return null;
  }

  const isoParsed = momentTZ.utc(value);
  return isoParsed.isValid() ? isoParsed : null;
}

export function getBrowserTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

export function getStoredPreferredTimezone(): string | null {
  try {
    const raw = localStorage.getItem(PERMISSIONS_STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as { preferred_timezone?: string };
    return parsed.preferred_timezone ?? null;
  } catch {
    return null;
  }
}

export function resolvePreferredTimezone(explicitTz?: string | null): string {
  return explicitTz ?? getStoredPreferredTimezone() ?? getBrowserTimezone();
}

function normalizeWallClockTime(time: string | null | undefined): string {
  if (!time) {
    return "00:00:00";
  }

  if (/^\d{2}:\d{2}:\d{2}$/.test(time)) {
    return time;
  }

  if (/^\d{2}:\d{2}$/.test(time)) {
    return `${time}:00`;
  }

  return time;
}

export function getNowInPreferredTimezone(explicitTz?: string | null): momentTZ.Moment {
  return momentTZ.tz(resolvePreferredTimezone(explicitTz));
}

export function parsePreferredWallClock(
  date: string | null | undefined,
  time: string | null | undefined,
  explicitTz?: string | null,
): momentTZ.Moment | null {
  if (!date) {
    return null;
  }

  const timezone = resolvePreferredTimezone(explicitTz);
  const parsed = momentTZ.tz(`${date} ${normalizeWallClockTime(time)}`, PREFERRED_WALL_CLOCK_DATETIME_FORMAT, timezone);

  return parsed.isValid() ? parsed : null;
}

export function getPreferredTimezoneMinDate(explicitTz?: string | null): Date {
  return getNowInPreferredTimezone(explicitTz).clone().startOf("day").toDate();
}

export function parseBackendDatetime(
  value: DatetimeInput,
  options: ParseBackendDatetimeOptions = {},
): momentTZ.Moment | null {
  if (isEmptyDatetimeValue(value)) {
    return null;
  }

  const assumeUtc = options.assumeUtc ?? true;

  if (assumeUtc) {
    return parseWithNaiveFormats(value, (input, format) => momentTZ.utc(input, format, true));
  }

  if (options.sourceTimezone) {
    return parseWithNaiveFormats(value, (input, format) => momentTZ.tz(input, format, options.sourceTimezone as string));
  }

  if (typeof value === "string") {
    return parseWithNaiveFormats(value, (input, format) => momentTZ(input, format, true));
  }

  const parsed = momentTZ(value);
  return parsed.isValid() ? parsed : null;
}

export function resolveDatetimeInTimezone(
  value: DatetimeInput,
  targetTimezone: string,
  options: ParseBackendDatetimeOptions = {},
): momentTZ.Moment | null {
  if (isEmptyDatetimeValue(value) || !targetTimezone) {
    return null;
  }

  const assumeUtc = options.assumeUtc ?? true;
  const { sourceTimezone } = options;

  if (assumeUtc || sourceTimezone === "UTC") {
    const parsed = parseWithNaiveFormats(value, (input, format) => momentTZ.utc(input, format, true));
    if (!parsed) {
      return null;
    }
    return parsed.tz(targetTimezone);
  }

  if (sourceTimezone) {
    const parsed = parseWithNaiveFormats(value, (input, format) => momentTZ.tz(input, format, sourceTimezone));
    if (!parsed) {
      return null;
    }
    return parsed.tz(targetTimezone);
  }

  if (typeof value === "string") {
    const parsedFromFormat = BACKEND_DATETIME_FORMATS.reduce<momentTZ.Moment | null>((result, format) => {
      if (result) {
        return result;
      }

      const attempt = momentTZ.tz(value, format, targetTimezone);
      return attempt.isValid() ? attempt : null;
    }, null);

    if (parsedFromFormat) {
      return parsedFromFormat;
    }
  }

  const parsed = momentTZ.tz(value, targetTimezone);
  return parsed.isValid() ? parsed : null;
}

export function formatInTimezone(
  value: DatetimeInput,
  targetTimezone: string,
  format: string,
  options: FormatInTimezoneOptions = {},
): string {
  const fallback = options.fallback ?? DEFAULT_DATETIME_FALLBACK;

  if (isEmptyDatetimeValue(value)) {
    return fallback;
  }

  const zonedDatetime = resolveDatetimeInTimezone(value, targetTimezone, options);
  if (!zonedDatetime) {
    return fallback;
  }

  let formatted = zonedDatetime.format(format);

  if (options.includeTimezoneLabel) {
    formatted += ` (${getTimezoneOffset(targetTimezone)})`;
  }

  return formatted;
}

export function formatInPreferredTimezone(
  value: DatetimeInput,
  format: string,
  options: FormatInTimezoneOptions = {},
): string {
  const timezone = resolvePreferredTimezone(options.timezone);
  return formatInTimezone(value, timezone, format, options);
}

export function formatInLocationTimezone(
  value: DatetimeInput,
  locationTimezone: string | null | undefined,
  format: string,
  options: FormatInTimezoneOptions = {},
): string {
  const fallback = options.fallback ?? DEFAULT_DATETIME_FALLBACK;

  if (!locationTimezone) {
    return fallback;
  }

  return formatInTimezone(value, locationTimezone, format, {
    ...options,
    assumeUtc: options.assumeUtc ?? false,
  });
}

// Extract the time from a datetime string
export function getTimeFromDateTime(dateTime: string) {
  return moment(dateTime).format(DateTimeFormat.timeFormat);
}

// Parse a date string using the specified format
export function parseDate(dateString: moment.MomentInput, format: moment.MomentFormatSpecification) {
  return moment(dateString, format);
}

// Format a date using the specified format
export function formatDate(date: moment.MomentInput, format: string) {
  return moment(date).format(format);
}

// Get the current date and time
export function getCurrentDateTime() {
  return moment();
}

// Get the start of the week
export function getStartOfWeek() {
  return moment().startOf("week");
}

// Add a certain amount of days to a date
export function addDays(date: moment.MomentInput, days: number) {
  return moment(date).add(days, "days");
}

// Check if the date is valid or not
export function isValidDate(date: moment.MomentInput) {
  return moment(date).isValid();
}

export function formatIfValid(date: moment.MomentInput, format: string) {
  if (isValidDate(date)) {
    return formatDate(date, format);
  }
  return date || "-";
}

// Check if the string matches the excel date string formats
export function isValidExcelDateString(dateString: string): boolean {
  const possibleDateFormats = ["DD-MM-YYYY", "D-M-YY", "DD-MM-YY", "DD-MM-YYYY", "YYYY-MM-DD"];
  return possibleDateFormats.some((format) => moment(dateString, format, true).isValid());
}

export function getDurationInWords(date1: string | moment.Moment, date2: string | moment.Moment) {
  const start = moment(date1);
  const end = moment(date2);

  if (!start.isValid() || !end.isValid()) return null;

  // Get absolute differences
  const diffInYears = Math.abs(end.diff(start, "years"));
  const diffInMonths = Math.abs(end.diff(start, "months")) % 12;
  const diffInDays = Math.abs(end.diff(start, "days")) % 30;
  const diffInHours = Math.abs(end.diff(start, "hours")) % 24;
  const diffInMinutes = Math.abs(end.diff(start, "minutes")) % 60;

  if (diffInYears > 0) {
    // If the years are different, return years and optionally months
    return `${diffInYears} year${diffInYears > 1 ? "s" : ""}${
      diffInMonths > 0 ? ` ${diffInMonths} month${diffInMonths > 1 ? "s" : ""}` : ""
    }`;
  }
  if (diffInMonths > 0) {
    // If the year is the same but months are different, return months and optionally days
    return `${diffInMonths} month${diffInMonths > 1 ? "s" : ""}${
      diffInDays > 0 ? ` ${diffInDays} day${diffInDays > 1 ? "s" : ""}` : ""
    }`;
  }
  if (diffInDays > 0) {
    // If the month is the same but days are different, return days and optionally hours
    return `${diffInDays} day${diffInDays > 1 ? "s" : ""}${
      diffInHours > 0 ? ` ${diffInHours} hr${diffInHours > 1 ? "s" : ""}` : ""
    }`;
  }
  // If the day is the same, return hours and optionally minutes
  return `${diffInHours} hr${diffInHours > 1 ? "s" : ""}${
    diffInMinutes > 0 ? ` ${diffInMinutes} min${diffInMinutes > 1 ? "s" : ""}` : ""
  }`;
}

function removeChineseCharacters(input: string) {
  // Use a regular expression to match and replace all occurrences of 日 and 月
  return input.replace(/[日月]/g, "");
}

export function getDateTimeInEnglish(dateString: string, { format = "DD MMM YYYY" }: { format?: string } = {}) {
  const currentLocale = moment.locale();
  moment.locale("en");
  const outputString = moment(dateString).format(format);
  moment.locale(currentLocale);
  return removeChineseCharacters(outputString);
}

/**
 * returns the offset value,
 * i.e: input = Asia/Singapore, Output: UTC+8
 */
export const getTimezoneOffset = (timezone: string) => {
  const offsetInMinutes = momentTZ.tz(timezone).utcOffset(); // Get offset in minutes
  const hours = Math.floor(Math.abs(offsetInMinutes) / 60);
  const minutes = Math.abs(offsetInMinutes) % 60;
  const sign = offsetInMinutes >= 0 ? "+" : "-";

  // Format to UTC+X:XX or UTC-X:XX
  return `UTC ${sign}${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
};

/**
 *
 * @param date1 first date to compare
 * @param date2 second date to compare
 * @param unit unit of time to get the difference in
 * @returns the difference between the two dates in the specified unit
 */
export const getDifferenceInDates = (date1: string, date2: string, unit: moment.unitOfTime.Diff) =>
  moment(date1).diff(moment(date2), unit);
